/**
 * Context for country data fetched from API
 * Provides descriptions, details, and photos throughout the app
 *
 * Uses stale-while-revalidate caching:
 * - Immediately shows cached data if available
 * - Fetches fresh data in background
 * - Updates cache with fresh data
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getPhotosGrouped, type Photo } from '@/lib/photo-api';

const API_BASE = import.meta.env.VITE_PHOTO_API_URL || 'https://where-in-the-world-photos.armincerf.workers.dev';

// localStorage cache keys
const CACHE_KEY_DATA = 'witw-country-data-v1';
const CACHE_KEY_PHOTOS = 'witw-country-photos-v1';

// Cache TTL: 24 hours (data rarely changes)
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Load data from localStorage cache.
 * Returns null if cache is missing or expired.
 */
function loadFromCache<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const entry: CacheEntry<T> = JSON.parse(cached);
    const age = Date.now() - entry.timestamp;

    // Return data regardless of age for stale-while-revalidate
    // But mark as stale if older than TTL
    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Check if cache is stale (older than TTL).
 */
function isCacheStale(key: string): boolean {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return true;

    const entry = JSON.parse(cached);
    return Date.now() - entry.timestamp > CACHE_TTL_MS;
  } catch {
    return true;
  }
}

/**
 * Save data to localStorage cache.
 */
function saveToCache<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (err) {
    // localStorage might be full or unavailable
    console.warn('Failed to cache data:', err);
  }
}

export interface CountryData {
  country_code: string;
  description: string | null;
  capital: string | null;
  population: string | null;
  currency: string | null;
  languages: string | null;
  area: string | null;
  // Comprehensive fields
  gdp: string | null;
  hdi: string | null;
  air_quality_avg: string | null;
  crime_avg: string | null;
  weather_winter: string | null;
  weather_spring: string | null;
  weather_summer: string | null;
  weather_autumn: string | null;
  timezone: string | null;
  positives: string | null; // JSON array
  negatives: string | null; // JSON array
  famous_for: string | null;
}

interface CountryDataContextValue {
  data: Record<string, CountryData>;
  photos: Record<string, Photo[]>;
  loading: boolean;
  error: string | null;
  getDescription: (code: string) => string | undefined;
  getDetails: (code: string) => Omit<CountryData, 'country_code' | 'description'> | undefined;
  getPhotos: (code: string) => Photo[];
}

const CountryDataContext = createContext<CountryDataContextValue | null>(null);

export function CountryDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<Record<string, CountryData>>({});
  const [photos, setPhotos] = useState<Record<string, Photo[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      // Step 1: Load from cache immediately (stale-while-revalidate)
      const cachedData = loadFromCache<Record<string, CountryData>>(CACHE_KEY_DATA);
      const cachedPhotos = loadFromCache<Record<string, Photo[]>>(CACHE_KEY_PHOTOS);

      if (cachedData && Object.keys(cachedData).length > 0) {
        setData(cachedData);
        setLoading(false); // Immediately show cached data
      }

      if (cachedPhotos && Object.keys(cachedPhotos).length > 0) {
        setPhotos(cachedPhotos);
      }

      // Step 2: Check if we need to revalidate
      const dataIsStale = isCacheStale(CACHE_KEY_DATA);
      const photosAreStale = isCacheStale(CACHE_KEY_PHOTOS);

      // If we have fresh cache for both, we're done
      if (!dataIsStale && !photosAreStale && cachedData && cachedPhotos) {
        setLoading(false);
        return;
      }

      // Step 3: Fetch fresh data in background
      try {
        const fetchPromises: Promise<unknown>[] = [];

        // Only fetch what's stale
        if (dataIsStale || !cachedData) {
          fetchPromises.push(
            fetch(`${API_BASE}/api/country-data?grouped=true`)
              .then(async (response) => {
                if (!response.ok) throw new Error('Failed to fetch country data');
                const result = await response.json();
                const freshData = result.data || {};

                if (mounted) {
                  setData(freshData);
                  saveToCache(CACHE_KEY_DATA, freshData);
                }
              })
          );
        }

        if (photosAreStale || !cachedPhotos) {
          fetchPromises.push(
            getPhotosGrouped()
              .then((freshPhotos) => {
                if (mounted) {
                  setPhotos(freshPhotos);
                  saveToCache(CACHE_KEY_PHOTOS, freshPhotos);
                }
              })
              .catch(() => {
                // Gracefully handle photo fetch failure
              })
          );
        }

        await Promise.all(fetchPromises);
      } catch (err) {
        console.error('Failed to revalidate country data:', err);
        // Only set error if we don't have cached data
        if (!cachedData) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const getDescription = (code: string): string | undefined => {
    return data[code]?.description ?? undefined;
  };

  const getDetails = (code: string): Omit<CountryData, 'country_code' | 'description'> | undefined => {
    const countryData = data[code];
    if (!countryData) return undefined;
    return {
      capital: countryData.capital,
      population: countryData.population,
      currency: countryData.currency,
      languages: countryData.languages,
      area: countryData.area,
      gdp: countryData.gdp,
      hdi: countryData.hdi,
      air_quality_avg: countryData.air_quality_avg,
      crime_avg: countryData.crime_avg,
      weather_winter: countryData.weather_winter,
      weather_spring: countryData.weather_spring,
      weather_summer: countryData.weather_summer,
      weather_autumn: countryData.weather_autumn,
      timezone: countryData.timezone,
      positives: countryData.positives,
      negatives: countryData.negatives,
      famous_for: countryData.famous_for,
    };
  };

  const getPhotos = (code: string): Photo[] => {
    return photos[code] || [];
  };

  return (
    <CountryDataContext.Provider value={{ data, photos, loading, error, getDescription, getDetails, getPhotos }}>
      {children}
    </CountryDataContext.Provider>
  );
}

export function useCountryDataContext() {
  const context = useContext(CountryDataContext);
  if (!context) {
    throw new Error('useCountryDataContext must be used within CountryDataProvider');
  }
  return context;
}

/**
 * Standalone function to get description (for use outside React components)
 * Falls back to empty string if not available
 */
export function getCountryDescriptionFromApi(code: string, data: Record<string, CountryData>): string | undefined {
  return data[code]?.description ?? undefined;
}
